import { useEffect, useRef } from "react";
import { Chapter, TemplateType } from "../types";
import { generateTemplate } from "../helpers/generateTemplate";
import { useTemplateData } from "./useTemplateData";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useSaveTemplate } from "./useSaveTemplate";
import { CreateMediaGenerationSettingsRequest } from "pages/media/types/";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";

const DEFAULT_MEDIA_SETTINGS: CreateMediaGenerationSettingsRequest = {
  chapters: [],
  width: 1080,
  height: 1920,
};

const getNewTemplateId = (
  currentChapter: Chapter,
  updatedMediaSettings: CreateMediaGenerationSettingsRequest
): string | undefined => {
  const chapterData = updatedMediaSettings.chapters.find(
    (ch) => ch.chapter_id === currentChapter.sectionId
  );

  switch (currentChapter.type) {
    case TemplateType.CHAPTER:
      return chapterData?.chapter_template_id ?? undefined;
    case TemplateType.TRANSITION:
      return chapterData?.transition_template_id ?? undefined;
    case TemplateType.INTRODUCTION:
      return updatedMediaSettings.introduction_template_id ?? undefined;
    case TemplateType.CLOSING:
      return updatedMediaSettings.closing_template_id ?? undefined;
    default:
      return undefined;
  }
};

/**
 * To reduce complexity when applying bulk actions to templates and also to avoid creating and deleting templates
 * unnecessarily, now each chapter, transition, opening and closing will have a template assigned to it.
 * This hook is used as a setup hook that runs in the background on loading an experience in the Video Editor.
 * It also takes care of updating the settings of old projects to this new way.
 */
export const useTemplateSetup = () => {
  const {
    templatesData: { organizationId, isFlashProject },
    mediaGenerationSettingsData: {
      mediaGenerationSettings,
      mediaAspectRatio: aspectRatio,
      saveMediaGenerationSettings,
      isMediaSettingsLoading,
      isMediaSettingsError,
    },
    storyboardSections,
    previewData: { previewSizes },
    isLoading,
  } = useTemplateData();

  const {
    defaultIntro,
    defaultClosing,
    defaultChapter,
    defaultTransition,
    templates,
    setTemplate,
    presenter,
    currentChapter,
    setCurrentChapterTemplateId,
  } = useTemplateEditorStore((store) => ({
    defaultIntro: store.defaultIntro,
    defaultClosing: store.defaultClosing,
    defaultChapter: store.defaultChapter,
    defaultTransition: store.defaultTransition,
    templates: store.templates,
    setTemplate: store.setTemplate,
    presenter: store.templateState.presenter,
    currentChapter: store.currentChapter,
    setCurrentChapterTemplateId: store.setCurrentChapterTemplateId,
  }));

  const { updates } = useTemplatesStatusStore((state) => ({
    updates: state.updates,
  }));

  const { createTemplate } = useSaveTemplate(organizationId);

  const setupStatus = useRef<{
    loading: boolean;
    isCompleted: boolean;
  }>({ loading: false, isCompleted: false });

  useEffect(() => {
    if (
      // This is a non-Flash chapter/transition/intro/closing setup-migration.
      // Flash projects already ship those template ids, so it would only
      // create nothing and fire a redundant media-settings save.
      isFlashProject ||
      !previewSizes ||
      isMediaSettingsLoading ||
      isMediaSettingsError ||
      storyboardSections.length === 0 ||
      isLoading ||
      setupStatus.current.loading ||
      setupStatus.current.isCompleted ||
      updates === 0
    )
      return;

    void (async () => {
      setupStatus.current = { loading: true, isCompleted: false };

      const updatedMediaSettings: CreateMediaGenerationSettingsRequest =
        mediaGenerationSettings
          ? {
              ...mediaGenerationSettings,
              chapters: [],
              chapter_template_id: undefined,
              transition_template_id: undefined,
            }
          : DEFAULT_MEDIA_SETTINGS;

      if (
        !mediaGenerationSettings?.introduction_template_id &&
        defaultIntro.intro.assetId
      ) {
        const template = generateTemplate(
          defaultIntro,
          TemplateType.INTRODUCTION,
          "Introduction",
          aspectRatio
        );
        const response = await createTemplate(template, false, false);
        if (response) {
          setTemplate(response.id, defaultIntro);
          updatedMediaSettings.introduction_template_id = response.id;
        }
      }

      if (
        !mediaGenerationSettings?.closing_template_id &&
        defaultClosing.closing.assetId
      ) {
        const template = generateTemplate(
          defaultClosing,
          TemplateType.CLOSING,
          "Closing",
          aspectRatio
        );
        const response = await createTemplate(template, false, false);
        if (response) {
          setTemplate(response.id, defaultClosing);
          updatedMediaSettings.closing_template_id = response.id;
        }
      }

      for (const section of storyboardSections) {
        const chapter = mediaGenerationSettings?.chapters.find(
          (chapter) => chapter.chapter_id === section.id
        );
        const updatedChapter = chapter
          ? { ...chapter }
          : { chapter_id: section.id, presenter_id: presenter.presenter?.id };

        if (!updatedChapter.chapter_template_id) {
          const commonChapterTemplate =
            mediaGenerationSettings?.chapter_template_id
              ? templates.get(mediaGenerationSettings.chapter_template_id)
              : undefined;

          const template = generateTemplate(
            commonChapterTemplate ?? defaultChapter,
            TemplateType.CHAPTER,
            section.topic,
            aspectRatio
          );
          const response = await createTemplate(template, false, false);
          if (response) {
            setTemplate(response.id, defaultChapter);
            updatedChapter.chapter_template_id = response.id;
          }
        }

        if (!updatedChapter.transition_template_id) {
          const commonTransitionTemplate =
            mediaGenerationSettings?.transition_template_id
              ? templates.get(mediaGenerationSettings.transition_template_id)
              : undefined;

          const template = generateTemplate(
            commonTransitionTemplate ?? defaultTransition,
            TemplateType.TRANSITION,
            section.topic,
            aspectRatio
          );
          const response = await createTemplate(template, false, false);
          if (response) {
            setTemplate(response.id, defaultTransition);
            updatedChapter.transition_template_id = response.id;
          }
        }

        updatedMediaSettings.chapters.push(updatedChapter);
      }

      // TODO: compare updated settings with current ones to avoid unnecessary server updates
      await saveMediaGenerationSettings(updatedMediaSettings);

      // Update currentChapter with the newly created template ID
      if (!currentChapter.templateId) {
        const newTemplateId = getNewTemplateId(
          currentChapter,
          updatedMediaSettings
        );
        if (newTemplateId) {
          setCurrentChapterTemplateId(newTemplateId);
        }
      }

      setupStatus.current = { loading: false, isCompleted: true };
    })();
  }, [
    isFlashProject,
    createTemplate,
    currentChapter,
    defaultChapter,
    defaultClosing,
    defaultIntro,
    defaultTransition,
    isLoading,
    aspectRatio,
    storyboardSections,
    mediaGenerationSettings,
    presenter.presenter?.id,
    previewSizes,
    setCurrentChapterTemplateId,
    setTemplate,
    templates,
    saveMediaGenerationSettings,
    isMediaSettingsLoading,
    isMediaSettingsError,
    updates,
  ]);
};
